package hk.com.vr360_demo2.webapp.component.marzipano.component

import js.coroutines.awaitCancellation
import kotlinx.browser.window
import react.FC
import react.Props
import react.useEffect
import react.useRef
import react.useState
import web.dom.Element
import web.html.HTMLDivElement

external interface DraftHotspotEditorOverlayProps : Props {
    var pendingDraftSceneId: String?
    var pendingDraftHotspotId: String?
    var viewerElement: HTMLDivElement?
    var resolveDraftHotspotElement: (sceneId: String, hotspotId: String) -> Element?
    var onCancel: () -> Unit
    var onConfirm: (sceneId: String, hotspotId: String) -> Unit
}

val DraftHotspotEditorOverlay = FC<DraftHotspotEditorOverlayProps> { props ->
    val animationFrameRef = useRef(-1)
    val (anchorX, setAnchorX) = useState<Double?>(null)
    val (anchorY, setAnchorY) = useState<Double?>(null)

    useEffect(dependencies = arrayOf(props.pendingDraftSceneId, props.pendingDraftHotspotId, props.viewerElement)) {
        val existingAnimationFrameId = animationFrameRef.current ?: -1
        if (existingAnimationFrameId >= 0) {
            window.cancelAnimationFrame(existingAnimationFrameId)
            animationFrameRef.current = -1
        }

        val draftSceneId = props.pendingDraftSceneId
        val draftHotspotId = props.pendingDraftHotspotId
        val viewerElement = props.viewerElement
        if (draftSceneId == null || draftHotspotId == null || viewerElement == null) {
            setAnchorX(null)
            setAnchorY(null)
            return@useEffect
        }

        fun scheduleAnchorUpdate() {
            val nextAnimationFrameId = window.requestAnimationFrame { _ ->
                scheduleAnchorUpdate()
            }
            animationFrameRef.current = nextAnimationFrameId

            val hotspotElement = props.resolveDraftHotspotElement(draftSceneId, draftHotspotId)
            if (hotspotElement == null) {
                setAnchorX(null)
                setAnchorY(null)
                return
            }
            val hotspotRect = hotspotElement.asDynamic().getBoundingClientRect()
            val viewerRect = viewerElement.asDynamic().getBoundingClientRect()

            val hotspotLeft = hotspotRect.left as Double
            val hotspotTop = hotspotRect.top as Double
            val hotspotWidth = hotspotRect.width as Double
            val hotspotHeight = hotspotRect.height as Double
            val hotspotRight = hotspotLeft + hotspotWidth
            val hotspotBottom = hotspotTop + hotspotHeight
            val viewerLeft = viewerRect.left as Double
            val viewerTop = viewerRect.top as Double
            val viewerWidth = viewerRect.width as Double
            val viewerHeight = viewerRect.height as Double
            val viewerRight = viewerLeft + viewerWidth
            val viewerBottom = viewerTop + viewerHeight

            val hasRenderableSize = hotspotWidth > 1.0 && hotspotHeight > 1.0
            val intersectsViewer = hotspotRight > viewerLeft &&
                    hotspotLeft < viewerRight &&
                    hotspotBottom > viewerTop &&
                    hotspotTop < viewerBottom
            if (!hasRenderableSize || !intersectsViewer) {
                setAnchorX(null)
                setAnchorY(null)
                return
            }

            val nextX = hotspotLeft - viewerLeft + (hotspotWidth / 2.0)
            val nextY = hotspotBottom - viewerTop + 8.0
            setAnchorX(nextX)
            setAnchorY(nextY)
        }

        scheduleAnchorUpdate()

        awaitCancellation {
            val frameId = animationFrameRef.current ?: -1
            if (frameId >= 0) {
                window.cancelAnimationFrame(frameId)
                animationFrameRef.current = -1
            }
        }
    }

    val showPanel = props.pendingDraftSceneId != null &&
            props.pendingDraftHotspotId != null &&
            anchorX != null &&
            anchorY != null
    if (!showPanel) return@FC

    PendingHotspotDraftPanel {
        this.anchorX = anchorX ?: 0.0
        this.anchorY = anchorY ?: 0.0
        onCancel = { props.onCancel() }
        onConfirm = {
            val draftSceneId = props.pendingDraftSceneId
            val draftHotspotId = props.pendingDraftHotspotId
            if (draftSceneId != null && draftHotspotId != null) {
                props.onConfirm(draftSceneId, draftHotspotId)
            }
        }
    }
}
